import { Tooltip } from 'antd';
import LinkButton from 'components/common/LinkButton/LinkButton';
import TermTooltip from 'components/common/TermTooltip/TermTooltip';
import { PADDOCK_KEY_PREFIX } from 'constants/routeConstants';
import {
  FARM_DIAGNOSTICS_LENS_WUE_TREND,
  FARM_DIAGNOSTICS_ROTATION_EXCLUDED_YEAR_REASON_LABELS,
  FARM_DIAGNOSTICS_ROTATION_EXCLUDED_YEARS_TOOLTIP_HEADING,
  FARM_DIAGNOSTICS_ROTATION_MISSING_RAINFALL_TOOLTIP_HEADING,
  FARM_DIAGNOSTICS_ROTATION_NO_DATA_TOOLTIP,
  FARM_DIAGNOSTICS_ROTATION_STANDING_BOTTOM_LABEL,
  FARM_DIAGNOSTICS_ROTATION_STANDING_NOTE,
  FARM_DIAGNOSTICS_ROTATION_STANDING_TOP_LABEL,
  FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_AREA,
  FARM_DIAGNOSTICS_ROTATION_FINANCIAL_RUE_TOOLTIP,
  FARM_DIAGNOSTICS_ROTATION_RUE_TOOLTIP,
  FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_FINANCIAL_WUE,
  FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_GM,
  FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_MANAGEMENT,
  FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_PADDOCK,
  FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_TREND,
  FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_WUE,
  FARM_DIAGNOSTICS_ROTATION_TREND_INSUFFICIENT_DATA_TOOLTIP,
  FARM_DIAGNOSTICS_ROTATION_TREND_RANGE_TOO_SHORT_TOOLTIP,
  FARM_DIAGNOSTICS_ROTATION_WUE_CROP_NOTE,
} from 'constants/uiConstants';
import React, { useMemo, useState } from 'react';
import { LuArrowDown, LuArrowUp, LuInfo } from 'react-icons/lu';
import { formatCurrency } from 'utils/CurrencyUtils';
import useFarmsLinkNavigate from 'pages/FarmsPage/hooks/useFarmsLinkNavigate';
import { formatNumber } from '../Insights/summary2/formatNumber';
import {
  computeRelativeStanding,
  type RelativeStanding,
  type RotationRow,
  type TrendUnavailableReason,
} from './computeRotationEfficiency';
import TrendBadge from './TrendBadge';

type SortKey =
  | 'paddockName'
  | 'areaHa'
  | 'rotationWue'
  | 'rotationGmPerHa'
  | 'rotationFinancialWue';

interface RotationEfficiencyTableProps {
  rows: RotationRow[];
  /** Paddock names as plain text, not links into the workspace — the plan preview (OP-2490). */
  readOnly?: boolean;
}

// Design-system status tints with a matching arrow — the colour never
// stands alone. Full class names so Tailwind keeps them.
const STANDING_CELL_CLASS: Record<RelativeStanding, string> = {
  top: 'bg-success-light',
  bottom: 'bg-danger-light',
};

const StandingMark: React.FC<{ standing: RelativeStanding | null }> = ({
  standing,
}) => {
  if (!standing) return <span className="inline-block w-3" />;
  const isTop = standing === 'top';
  return (
    <>
      {isTop ? (
        <LuArrowUp size={12} aria-hidden className="inline text-success" />
      ) : (
        <LuArrowDown
          size={12}
          aria-hidden
          className="inline text-danger-dark"
        />
      )}
      <span className="sr-only">
        {isTop
          ? FARM_DIAGNOSTICS_ROTATION_STANDING_TOP_LABEL
          : FARM_DIAGNOSTICS_ROTATION_STANDING_BOTTOM_LABEL}
      </span>
    </>
  );
};

const standingCellClass = (standing: RelativeStanding | null | undefined) =>
  standing ? STANDING_CELL_CLASS[standing] : '';

const describeTrendUnavailableReason = (reason: TrendUnavailableReason): string => {
  switch (reason.kind) {
    case 'rangeTooShort':
      return FARM_DIAGNOSTICS_ROTATION_TREND_RANGE_TOO_SHORT_TOOLTIP;
    case 'insufficientData':
      return FARM_DIAGNOSTICS_ROTATION_TREND_INSUFFICIENT_DATA_TOOLTIP;
  }
};

const NoDataCell: React.FC<{ reason: string }> = ({ reason }) => (
  <Tooltip title={reason}>
    <span className="cursor-help font-normal text-gray-muted">—</span>
  </Tooltip>
);

const SortableHeader: React.FC<{
  label: string;
  /** Explains a term in the label on hover (e.g. RUE). */
  tooltip?: string;
  sortKey: SortKey;
  activeKey: SortKey;
  direction: 'asc' | 'desc';
  onClick: (key: SortKey) => void;
  align?: 'left' | 'right';
}> = ({ label, tooltip, sortKey, activeKey, direction, onClick, align = 'left' }) => (
  <th
    className={`px-2 pb-2 text-sm font-bold text-charcoal ${align === 'right' ? 'text-right' : 'text-left'}`}
  >
    <button
      type="button"
      className={`inline-flex cursor-pointer items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}
      onClick={() => onClick(sortKey)}
    >
      <TermTooltip tooltip={tooltip}>{label}</TermTooltip>
      {activeKey === sortKey &&
        (direction === 'desc' ? (
          <LuArrowDown size={12} />
        ) : (
          <LuArrowUp size={12} />
        ))}
    </button>
  </th>
);

/**
 * Sortable Farm/Org-level paddock comparison table — one row per paddock,
 * never a blended Farm/Org total row. In the WUE, GM and Financial WUE
 * columns the top third of paddocks shown gets a green tint and ▲, the
 * bottom third a red tint and ▼, and the middle stays plain
 * (computeRelativeStanding) — colour only where there's a call to make, and
 * judged per column so trade-offs show, e.g. top on WUE but bottom on
 * margin (OP-2490).
 */
const RotationEfficiencyTable: React.FC<RotationEfficiencyTableProps> = ({
  rows,
  readOnly = false,
}) => {
  const navigateToFarmsView = useFarmsLinkNavigate();
  const standings = useMemo(
    () => ({
      rotationWue: computeRelativeStanding(rows, 'rotationWue'),
      rotationGmPerHa: computeRelativeStanding(rows, 'rotationGmPerHa'),
      rotationFinancialWue: computeRelativeStanding(
        rows,
        'rotationFinancialWue'
      ),
    }),
    [rows]
  );
  const [sort, setSort] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>(
    {
      key: 'rotationWue',
      direction: 'desc',
    }
  );

  const handleSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
        : { key, direction: 'desc' }
    );
  };

  const sortedRows = useMemo(() => {
    const withNullsLast = (value: number | string | null) => value == null;
    return [...rows].sort((a, b) => {
      const aValue = a[sort.key];
      const bValue = b[sort.key];
      if (withNullsLast(aValue) && withNullsLast(bValue)) return 0;
      if (withNullsLast(aValue)) return 1;
      if (withNullsLast(bValue)) return -1;
      const cmp =
        typeof aValue === 'string' && typeof bValue === 'string'
          ? aValue.localeCompare(bValue, undefined, { sensitivity: 'base' })
          : (aValue as number) - (bValue as number);
      return sort.direction === 'desc' ? -cmp : cmp;
    });
  }, [rows, sort]);

  return (
    <div className="flex flex-col gap-3">
      {/* print-colour-exact: browsers drop background colours when printing
          unless told otherwise, which would erase the top / bottom tints. The
          arrows carry the meaning either way. */}
      <div className="summary-grid-scroll print-colour-exact w-0 min-w-full overflow-x-auto">
        {/* stack-table-wide: in the plan preview, under 48rem of report
            width each paddock stacks — name on top, figures under their
            data-label — instead of scrolling (App.css). The workspace has no
            size container, so it keeps the table. */}
        <table className="stack-table-wide w-max border-collapse text-base">
          <thead>
            <tr>
              <SortableHeader
                label={FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_PADDOCK}
                sortKey="paddockName"
                activeKey={sort.key}
                direction={sort.direction}
                onClick={handleSort}
              />
              <SortableHeader
                label={FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_AREA}
                sortKey="areaHa"
                activeKey={sort.key}
                direction={sort.direction}
                onClick={handleSort}
                align="right"
              />
              <th className="px-2 pb-2 text-left text-sm font-bold text-charcoal">
                {FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_MANAGEMENT}
              </th>
              <SortableHeader
                label={FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_WUE}
                tooltip={FARM_DIAGNOSTICS_ROTATION_RUE_TOOLTIP}
                sortKey="rotationWue"
                activeKey={sort.key}
                direction={sort.direction}
                onClick={handleSort}
                align="right"
              />
              <SortableHeader
                label={FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_GM}
                sortKey="rotationGmPerHa"
                activeKey={sort.key}
                direction={sort.direction}
                onClick={handleSort}
                align="right"
              />
              <SortableHeader
                label={FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_FINANCIAL_WUE}
                tooltip={FARM_DIAGNOSTICS_ROTATION_FINANCIAL_RUE_TOOLTIP}
                sortKey="rotationFinancialWue"
                activeKey={sort.key}
                direction={sort.direction}
                onClick={handleSort}
                align="right"
              />
              <th className="px-2 pb-2 text-left text-sm font-bold text-charcoal">
                {FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_TREND}
              </th>
              <th className="w-8 px-2 pb-2" />
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => {
              const missingRainfallYears = row.excludedYears.filter(
                (y) => y.reason === 'missingRainfall'
              );
              return (
                <tr
                  key={row.paddockId}
                  className="border-b border-gray-bright hover:bg-rowHover"
                >
                  <td className="stack-primary py-1.5 pl-2 pr-6 text-base font-semibold text-charcoal">
                    {readOnly ? (
                      row.paddockName
                    ) : (
                      <LinkButton
                        className="text-left text-base"
                        onClick={() =>
                          navigateToFarmsView({
                            nodeKey: `${PADDOCK_KEY_PREFIX}${row.paddockId}`,
                            diagnosticsLens: FARM_DIAGNOSTICS_LENS_WUE_TREND,
                          })
                        }
                      >
                        {row.paddockName}
                      </LinkButton>
                    )}
                  </td>
                  <td
                    data-label={FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_AREA}
                    className="px-2 py-1.5 text-right tabular-nums text-charcoal"
                  >
                    {formatNumber(row.areaHa)}
                  </td>
                  <td
                    data-label={FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_MANAGEMENT}
                    className="px-2 py-1.5 text-base text-charcoal"
                  >
                    {row.managementLabels.length > 0
                      ? row.managementLabels.join(', ')
                      : '—'}
                  </td>
                  <td
                    data-label={FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_WUE}
                    className={`px-2 py-1.5 text-right tabular-nums text-charcoal ${standingCellClass(standings.rotationWue[row.paddockId])}`}
                  >
                    {row.rotationWue != null ? (
                      <span className="inline-flex items-center gap-1">
                        {formatNumber(row.rotationWue)}
                        <StandingMark
                          standing={standings.rotationWue[row.paddockId]}
                        />
                      </span>
                    ) : missingRainfallYears.length > 0 ? (
                      <NoDataCell
                        reason={`${FARM_DIAGNOSTICS_ROTATION_MISSING_RAINFALL_TOOLTIP_HEADING} ${missingRainfallYears.map((y) => y.year).join(', ')}`}
                      />
                    ) : (
                      <NoDataCell reason={FARM_DIAGNOSTICS_ROTATION_NO_DATA_TOOLTIP} />
                    )}
                  </td>
                  <td
                    data-label={FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_GM}
                    className={`px-2 py-1.5 text-right tabular-nums text-charcoal ${standingCellClass(standings.rotationGmPerHa[row.paddockId])}`}
                  >
                    {row.rotationGmPerHa != null ? (
                      <span className="inline-flex items-center gap-1">
                        {formatCurrency(row.rotationGmPerHa)}
                        <StandingMark
                          standing={standings.rotationGmPerHa[row.paddockId]}
                        />
                      </span>
                    ) : (
                      <NoDataCell reason={FARM_DIAGNOSTICS_ROTATION_NO_DATA_TOOLTIP} />
                    )}
                  </td>
                  <td
                    data-label={FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_FINANCIAL_WUE}
                    className={`px-2 py-1.5 text-right tabular-nums text-charcoal ${standingCellClass(standings.rotationFinancialWue[row.paddockId])}`}
                  >
                    {row.rotationFinancialWue != null ? (
                      <span className="inline-flex items-center gap-1">
                        {formatCurrency(row.rotationFinancialWue)}
                        <StandingMark
                          standing={standings.rotationFinancialWue[row.paddockId]}
                        />
                      </span>
                    ) : missingRainfallYears.length > 0 ? (
                      <NoDataCell
                        reason={`${FARM_DIAGNOSTICS_ROTATION_MISSING_RAINFALL_TOOLTIP_HEADING} ${missingRainfallYears.map((y) => y.year).join(', ')}`}
                      />
                    ) : (
                      <NoDataCell reason={FARM_DIAGNOSTICS_ROTATION_NO_DATA_TOOLTIP} />
                    )}
                  </td>
                  <td
                    data-label={FARM_DIAGNOSTICS_ROTATION_TABLE_COLUMN_TREND}
                    className="px-2 py-1.5"
                  >
                    {row.trendUnavailableReason ? (
                      <NoDataCell
                        reason={describeTrendUnavailableReason(
                          row.trendUnavailableReason
                        )}
                      />
                    ) : (
                      <TrendBadge trend={row.trend} />
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    {row.excludedYears.length > 0 && (
                      <Tooltip
                        title={
                          <div className="flex flex-col gap-0.5">
                            <span className="font-semibold text-white">
                              {
                                FARM_DIAGNOSTICS_ROTATION_EXCLUDED_YEARS_TOOLTIP_HEADING
                              }
                            </span>
                            {row.excludedYears.map((y) => (
                              <span key={y.year} className="text-gray-bright">
                                {y.year}:{' '}
                                {
                                  FARM_DIAGNOSTICS_ROTATION_EXCLUDED_YEAR_REASON_LABELS[
                                    y.reason
                                  ]
                                }
                              </span>
                            ))}
                          </div>
                        }
                      >
                        <LuInfo
                          size={14}
                          className="cursor-help text-gray-medium"
                        />
                      </Tooltip>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-dark">
          <span className="inline-flex items-center gap-1">
            <span className="inline-flex h-4 w-5 items-center justify-center bg-success-light">
              <LuArrowUp size={12} aria-hidden className="text-success" />
            </span>
            {FARM_DIAGNOSTICS_ROTATION_STANDING_TOP_LABEL}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-flex h-4 w-5 items-center justify-center bg-danger-light">
              <LuArrowDown size={12} aria-hidden className="text-danger-dark" />
            </span>
            {FARM_DIAGNOSTICS_ROTATION_STANDING_BOTTOM_LABEL}
          </span>
          <span className="text-gray-muted">
            ({FARM_DIAGNOSTICS_ROTATION_STANDING_NOTE})
          </span>
        </div>
        <p className="text-sm text-gray-muted">
          {FARM_DIAGNOSTICS_ROTATION_WUE_CROP_NOTE}
        </p>
      </div>
    </div>
  );
};

export default RotationEfficiencyTable;
